import * as React from 'react';

import {
  DataGrid,
  Pagination,
  type PaginationProps,
  Select,
  Button,
  ListScreenLayout,
  type ListStatsCardProps,
  DataGridColumn,
  Menu,
  type MenuItem,
  DialogOverlay,
  DialogContent,
  DialogHeader,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  LucidePlus,
  LucideCalendarClock,
  LucideCheckCircle,
  LucidePauseCircle,
  LucideMoreHorizontal,
  LucideEdit,
  LucidePlay,
  LucidePause,
  LucideAlertTriangle,
  LucideTrash2,
} from 'lucide-react';

import {
  activateCycleCountScheduleAtom,
  deactivateCycleCountScheduleAtom,
  deleteCycleCountScheduleAtom,
} from '../../_graphql/mutationCycleCountSchedule';
import type {
  CycleCountScheduleRecord,
  CycleCountScheduleSortOption,
} from '../../_graphql/queryCycleCountSchedules';
import * as state from '../../_state/cycleCountSchedulesList.state';
import { $remoteFacilities } from '../../_state/facilities.state';
import { useTranslations } from '../../i18n/useTranslations';

import { CycleCountScheduleDialog } from './CycleCountScheduleDialog';

const COLUMN_TO_SORT_MAP: Record<
  string,
  {
    asc: CycleCountScheduleSortOption;
    desc: CycleCountScheduleSortOption;
  }
> = {
  name: {
    asc: 'NameAsc',
    desc: 'NameDesc',
  },
  frequency: {
    asc: 'FrequencyAsc',
    desc: 'FrequencyDesc',
  },
  createdAt: {
    asc: 'CreatedAtAsc',
    desc: 'CreatedAtDesc',
  },
};

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return '\u2014';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '\u2014';
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const renderNameCell = ({ row }: { row: CycleCountScheduleRecord }) => (
  <span className="font-medium">{row.name}</span>
);

const renderFacilityCell = ({ row }: { row: CycleCountScheduleRecord }) => (
  <span className="text-sm">{row.facilityName || '\u2014'}</span>
);

const renderFrequencyCell = ({ row }: { row: CycleCountScheduleRecord }) => (
  <span className="text-sm capitalize">{row.frequency}</span>
);

const renderScopeTypeCell = ({ row }: { row: CycleCountScheduleRecord }) => (
  <span className="text-sm capitalize">{row.scopeType}</span>
);

const renderActiveCell = ({ row }: { row: CycleCountScheduleRecord }) => (
  <span
    className={
      row.isActive ? 'text-sm text-success' : 'text-sm text-neutral/50'
    }
  >
    {row.isActive ? 'Active' : 'Inactive'}
  </span>
);

const renderLastGeneratedCell = ({
  row,
}: {
  row: CycleCountScheduleRecord;
}) => <span className="text-sm">{formatDate(row.lastGeneratedAt)}</span>;

export const CycleCountSchedulesListView = () => {
  const t = useTranslations();
  const toast = useToast();

  const [selectedFacility, setSelectedFacility] = useAtom(
    state.$selectedFacility,
  );
  const [offset, setOffset] = useAtom(state.$offset);
  const [limit, setLimit] = useAtom(state.$limit);
  const setCurrentSorting = useSetAtom(state.$currentSorting);

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useAtom(
    state.$isCreateDialogOpen,
  );
  const [editingSchedule, setEditingSchedule] =
    React.useState<CycleCountScheduleRecord | null>(null);
  const [deletingSchedule, setDeletingSchedule] =
    React.useState<CycleCountScheduleRecord | null>(null);

  const { data, isFetching, loadingState, error, refetch } = useAtomValue(
    state.$remoteCycleCountSchedules,
  );

  const { data: facilitiesData } = useAtomValue($remoteFacilities);
  const facilities = React.useMemo(
    () => facilitiesData?.facilities ?? [],
    [facilitiesData?.facilities],
  );

  const activateSchedule = useSetAtom(activateCycleCountScheduleAtom({}));
  const deactivateSchedule = useSetAtom(deactivateCycleCountScheduleAtom({}));
  const deleteSchedule = useSetAtom(deleteCycleCountScheduleAtom({}));

  const schedules = data?.schedules ?? [];
  const total = data?.totalCount ?? 0;

  const activeCount = React.useMemo(
    () => schedules.filter((s) => s.isActive).length,
    [schedules],
  );
  const inactiveCount = React.useMemo(
    () => schedules.filter((s) => !s.isActive).length,
    [schedules],
  );

  const handlePaginationChange: PaginationProps['onChange'] = ({
    limit: newLimit,
    offset: newOffset,
  }) => {
    setLimit(newLimit);
    setOffset(newOffset);
  };

  const handleColumnSort = React.useCallback(
    (columnId: string, direction: 'ascending' | 'descending' | null) => {
      if (direction === null) {
        setCurrentSorting(['NameAsc']);
      } else {
        const sortMap = COLUMN_TO_SORT_MAP[columnId];
        if (sortMap) {
          const sortOption =
            direction === 'ascending' ? sortMap.asc : sortMap.desc;
          setCurrentSorting([sortOption]);
        }
      }
      setOffset(0);
    },
    [setCurrentSorting, setOffset],
  );

  const handleCreateClick = React.useCallback(() => {
    setIsCreateDialogOpen(true);
  }, [setIsCreateDialogOpen]);

  const handleDialogClose = React.useCallback(() => {
    setIsCreateDialogOpen(false);
    setEditingSchedule(null);
  }, [setIsCreateDialogOpen]);

  const handleDialogSuccess = React.useCallback(() => {
    refetch();
    handleDialogClose();
  }, [refetch, handleDialogClose]);

  const handleEdit = React.useCallback((schedule: CycleCountScheduleRecord) => {
    setEditingSchedule(schedule);
  }, []);

  const handleToggleActive = React.useCallback(
    async (schedule: CycleCountScheduleRecord) => {
      try {
        if (schedule.isActive) {
          await deactivateSchedule({ id: schedule.id });
          toast.success(t.cycleCountSchedulesView.actions.deactivateSuccess);
        } else {
          await activateSchedule({ id: schedule.id });
          toast.success(t.cycleCountSchedulesView.actions.activateSuccess);
        }
        refetch();
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : t.cycleCountSchedulesView.dialog.unknownError;
        toast.error(
          t.cycleCountSchedulesView.actions.toggleError(errorMessage),
        );
      }
    },
    [activateSchedule, deactivateSchedule, toast, t, refetch],
  );

  const handleDelete = React.useCallback(
    (schedule: CycleCountScheduleRecord) => {
      setDeletingSchedule(schedule);
    },
    [],
  );

  const handleConfirmDelete = React.useCallback(async () => {
    if (!deletingSchedule) return;
    try {
      await deleteSchedule({ id: deletingSchedule.id });
      toast.success(t.cycleCountSchedulesView.actions.deleteSuccess);
      refetch();
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : t.cycleCountSchedulesView.dialog.unknownError;
      toast.error(t.cycleCountSchedulesView.actions.deleteError(errorMessage));
    } finally {
      setDeletingSchedule(null);
    }
  }, [deletingSchedule, deleteSchedule, toast, t, refetch]);

  const columns =
    React.useMemo((): DataGridColumn<CycleCountScheduleRecord>[] => {
      const cols: DataGridColumn<CycleCountScheduleRecord>[] = [
        {
          key: 'name',
          header: t.cycleCountSchedulesView.table.columns.name,
          cell: renderNameCell,
          width: 200,
          sortable: true,
        },
        {
          key: 'facility',
          header: t.cycleCountSchedulesView.table.columns.facility,
          cell: renderFacilityCell,
          width: 180,
        },
        {
          key: 'frequency',
          header: t.cycleCountSchedulesView.table.columns.frequency,
          cell: renderFrequencyCell,
          width: 120,
          sortable: true,
        },
        {
          key: 'scopeType',
          header: t.cycleCountSchedulesView.table.columns.scopeType,
          cell: renderScopeTypeCell,
          width: 120,
        },
        {
          key: 'isActive',
          header: t.cycleCountSchedulesView.table.columns.isActive,
          cell: renderActiveCell,
          width: 100,
        },
        {
          key: 'lastGeneratedAt',
          header: t.cycleCountSchedulesView.table.columns.lastGeneratedAt,
          cell: renderLastGeneratedCell,
          width: 150,
        },
        {
          key: 'actions',
          header: '',
          width: 60,
          pinned: 'right',
          cell: ({ row }) => {
            const schedule = row as CycleCountScheduleRecord;

            const menuItems: MenuItem[] = [
              {
                label: (
                  <div className="flex items-center gap-2">
                    <LucideEdit className="size-4" />
                    {t.cycleCountSchedulesView.actions.edit}
                  </div>
                ),
                onAction: () => handleEdit(schedule),
              },
              {
                label: (
                  <div className="flex items-center gap-2">
                    {schedule.isActive ? (
                      <LucidePause className="size-4" />
                    ) : (
                      <LucidePlay className="size-4" />
                    )}
                    {schedule.isActive
                      ? t.cycleCountSchedulesView.actions.deactivate
                      : t.cycleCountSchedulesView.actions.activate}
                  </div>
                ),
                onAction: () => handleToggleActive(schedule),
              },
              'separator',
              {
                label: (
                  <div className="flex items-center gap-2 text-failure">
                    <LucideTrash2 className="size-4" />
                    {t.cycleCountSchedulesView.actions.delete}
                  </div>
                ),
                onAction: () => handleDelete(schedule),
              },
            ];

            return (
              <div className="flex justify-center">
                <Menu
                  trigger={
                    <Button
                      size="icon"
                      icon={<LucideMoreHorizontal />}
                      variant="outline"
                    />
                  }
                  items={menuItems}
                />
              </div>
            );
          },
        },
      ];

      return cols;
    }, [t, handleEdit, handleToggleActive, handleDelete]);

  const facilityOptions = React.useMemo(
    () => [
      {
        key: 'all',
        label: t.cycleCountSchedulesView.filters.allFacilities,
      },
      ...facilities.map((f) => ({ key: f.id, label: f.name })),
    ],
    [facilities, t],
  );

  const statsColumns = React.useMemo(
    (): ListStatsCardProps['stats'] => [
      {
        key: 'totalSchedules',
        label: t.cycleCountSchedulesView.summary.totalSchedules,
        value: total,
        icon: <LucideCalendarClock />,
        color: 'primary',
      },
      {
        key: 'activeSchedules',
        label: t.cycleCountSchedulesView.summary.activeSchedules,
        value: activeCount,
        icon: <LucideCheckCircle />,
        color: 'success',
      },
      {
        key: 'inactiveSchedules',
        label: t.cycleCountSchedulesView.summary.inactiveSchedules,
        value: inactiveCount,
        icon: <LucidePauseCircle />,
        color: 'warning',
      },
    ],
    [total, activeCount, inactiveCount, t],
  );

  const emptyPanelProps = {
    title: t.cycleCountSchedulesView.emptyState.title,
    description: <span>{t.cycleCountSchedulesView.emptyState.subtitle}</span>,
    centered: true,
  };

  return (
    <div className="@CycleCountSchedulesListView flex h-full flex-col">
      <ListScreenLayout
        filters={
          <Select
            value={selectedFacility || 'all'}
            onChange={(key) => {
              setSelectedFacility(key === 'all' ? undefined : String(key));
              setOffset(0);
            }}
            options={facilityOptions}
            className="w-48"
          />
        }
        actions={
          <Button
            icon={<LucidePlus />}
            color="primary"
            onPress={handleCreateClick}
          >
            {t.cycleCountSchedulesView.createSchedule}
          </Button>
        }
        stats={statsColumns}
        footer={
          total > limit ? (
            <Pagination
              limit={limit}
              offset={offset}
              total={total}
              onChange={handlePaginationChange}
            />
          ) : undefined
        }
      >
        <DataGrid<CycleCountScheduleRecord>
          isLoading={loadingState.isInitialLoading || isFetching}
          columns={columns}
          dataSource={schedules}
          rowKey="id"
          aria-label={t.cycleCountSchedulesView.table.aria}
          onColumnSort={handleColumnSort}
          className="w-full text-sm"
          fullWidth
          hasError={!!error}
          errorPanelProps={
            error
              ? {
                  description: t.cycleCountSchedulesView.content.error(
                    error.message,
                  ),
                  centered: true,
                }
              : undefined
          }
          emptyPanelProps={emptyPanelProps}
        />
      </ListScreenLayout>

      <CycleCountScheduleDialog
        isOpen={isCreateDialogOpen || !!editingSchedule}
        onClose={handleDialogClose}
        onSuccess={handleDialogSuccess}
        editingSchedule={editingSchedule}
      />

      {deletingSchedule && (
        <DialogOverlay isOpen className="@DeleteScheduleDialog">
          <DialogContent>
            <DialogHeader>
              <div className="flex items-center gap-2">
                <LucideAlertTriangle className="size-5 text-failure" />
                <span className="text-base font-semibold">
                  {t.cycleCountSchedulesView.confirmDelete.title}
                </span>
              </div>
            </DialogHeader>
            <div className="flex flex-col gap-2 px-4">
              <div className="text-base text-neutral/50">
                {t.cycleCountSchedulesView.confirmDelete.description(
                  deletingSchedule.name,
                )}
              </div>
              <div className="flex justify-end gap-2 py-4">
                <Button
                  variant="text"
                  color="neutral"
                  onPress={() => setDeletingSchedule(null)}
                >
                  {t.cycleCountSchedulesView.confirmDelete.cancel}
                </Button>
                <Button
                  variant="solid"
                  color="failure"
                  onPress={handleConfirmDelete}
                  icon={<LucideTrash2 className="size-4" />}
                >
                  {t.cycleCountSchedulesView.confirmDelete.confirm}
                </Button>
              </div>
            </div>
          </DialogContent>
        </DialogOverlay>
      )}
    </div>
  );
};
